import { useEffect, useState } from "react";

import { api, ApiError, type Tournament, type TournamentWhatsappGroup } from "../lib/api";
import {
  MAX_WHATSAPP_GROUPS,
  emptyWhatsappRow,
  isHttpUrl,
  prepareWhatsappGroups,
  splitWhatsappGroups,
  upgradeWhatsappGroupUrl,
  whatsappGroupLinkWarning,
  type WhatsappRowDraft,
} from "../lib/tournamentExtras";
import FieldHint from "./FieldHint";

type Props = {
  token: string;
  tournament: Tournament;
  groups: TournamentWhatsappGroup[] | null | undefined;
  /** Avisa al padre lo que quedó guardado (link 1 + lista completa). */
  onSaved: (link: string | null, groups: TournamentWhatsappGroup[]) => void;
};

/**
 * Editor de los grupos de WhatsApp del torneo (Eduardo 2026-10-12: vive solo
 * en Comunicación). Guarda SOLO estos campos: el link 1 por el PUT del torneo
 * (`whatsapp_group_link`, nada más) y el nombre 1 + extras por su ruta propia.
 */
export default function WhatsappGroupsEditor({ token, tournament, groups, onSaved }: Props) {
  const [link, setLink] = useState("");
  const [firstLabel, setFirstLabel] = useState("");
  const [extra, setExtra] = useState<WhatsappRowDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const first = (tournament.whatsapp_group_link ?? "").trim();
    const split = splitWhatsappGroups(groups, Boolean(first));
    setLink(first);
    setFirstLabel(split.firstLabel);
    setExtra(split.extra);
    // Solo al cambiar de torneo: un guardado no debe pisar lo que se escribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament.id]);

  const hasFirst = Boolean(link.trim());
  const extraStart = hasFirst ? 2 : 1;

  function updateRow(index: number, patch: Partial<WhatsappRowDraft>) {
    setExtra((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  async function handleSave() {
    setError(null);
    setNotice(null);
    if (link.trim() && !isHttpUrl(link.trim())) {
      setError("El link del Grupo WhatsApp debe iniciar con http:// o https://.");
      return;
    }
    const prepared = prepareWhatsappGroups(firstLabel, extra, hasFirst);
    if (!prepared.ok) {
      setError(prepared.error);
      return;
    }
    setSaving(true);
    try {
      const nextLink = upgradeWhatsappGroupUrl(link) || null;
      await api.tournaments.update(token, tournament.id, { whatsapp_group_link: nextLink });
      const response = await api.tournaments.saveWhatsappGroups(token, tournament.id, prepared.payload);
      setLink(nextLink ?? "");
      onSaved(nextLink, response.data.whatsapp_groups);
      setNotice("Grupos de WhatsApp guardados.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudieron guardar los grupos." : "Error de red. Intenta otra vez.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card wiz-card wiz-grupos">
      <header className="wiz-card__head">
        <h2 className="hint-label">
          Grupos de WhatsApp
          <FieldHint label="¿Para qué sirven los grupos de WhatsApp?">
            Si tú creas un grupo de WhatsApp para el torneo, pega aquí su link de invitación. Torneos123 se lo manda a cada jugador en el aviso de bienvenida y cuando se confirma su pago, para que entre al grupo. Puedes poner varios grupos (por ejemplo uno por categoría) con un nombre para cada uno. Si no usas grupos, deja esto vacío.
          </FieldHint>
        </h2>
        <p>Opcional · para organizadores que arman su propio grupo de WhatsApp del torneo.</p>
      </header>
      <div className="setup-call-grid setup-call-grid--two">
        <div className="setup-call-form__wide setup-extra-row setup-extra-row--group-first">
          <label>
            <span className="hint-label">
              Link Grupo WhatsApp
              <FieldHint>
                El jugador lo ve hasta que su pago está confirmado — antes se le
                muestra "el link se te compartirá al confirmar tu pago".
              </FieldHint>
            </span>
            <input
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              disabled={saving}
              placeholder="https://chat.whatsapp.com/..."
            />
          </label>
          <label>
            <span className="hint-label">
              Nombre del grupo (opcional)
              <FieldHint>Con varios grupos, el jugador ve cada botón con su nombre (p. ej. «Grupo Roja»).</FieldHint>
            </span>
            <input
              type="text"
              maxLength={80}
              value={firstLabel}
              onChange={(e) => setFirstLabel(e.target.value)}
              disabled={saving}
              placeholder="Ej. Grupo Roja"
            />
          </label>
          {whatsappGroupLinkWarning(link) ? (
            <small className="field-warning setup-extra-row__warning" role="status">
              ⚠️ {whatsappGroupLinkWarning(link)}
            </small>
          ) : null}
        </div>
        {extra.map((row, index) => (
          <div className="setup-call-form__wide setup-extra-row setup-extra-row--group" key={`grupo-${index}`}>
            <label>
              Link grupo WhatsApp {index + extraStart}
              <input
                type="url"
                value={row.url}
                onChange={(e) => updateRow(index, { url: e.target.value })}
                disabled={saving}
                placeholder="https://chat.whatsapp.com/..."
              />
            </label>
            <label>
              Nombre del grupo {index + extraStart} (opcional)
              <input
                type="text"
                maxLength={80}
                value={row.label}
                onChange={(e) => updateRow(index, { label: e.target.value })}
                disabled={saving}
                placeholder="Ej. Papás Verde"
              />
            </label>
            <button
              type="button"
              className="btn btn--ghost setup-venue-row__remove"
              onClick={() => setExtra((current) => current.filter((_, i) => i !== index))}
              disabled={saving}
              aria-label={`Quitar grupo de WhatsApp ${index + extraStart}`}
            >
              Quitar
            </button>
            {whatsappGroupLinkWarning(row.url) ? (
              <small className="field-warning setup-extra-row__warning" role="status">
                ⚠️ {whatsappGroupLinkWarning(row.url)}
              </small>
            ) : null}
          </div>
        ))}
        <div className="setup-call-form__wide hint-label">
          <button
            type="button"
            className="btn btn--ghost wiz-add"
            onClick={() => setExtra((current) => [...current, emptyWhatsappRow()])}
            disabled={saving || extra.length + 1 >= MAX_WHATSAPP_GROUPS}
          >
            + Agregar otro grupo de WhatsApp
          </button>
          <FieldHint label="¿Para qué agregar otro grupo de WhatsApp?">
            Si el torneo tiene más de un grupo (p. ej. «Grupo Roja» y «Papás Verde»), agrega cada
            uno con su nombre. Igual que el primero, el jugador los ve hasta que su pago está
            confirmado, y van en los mensajes de pago confirmado y de bienvenida.
          </FieldHint>
        </div>
      </div>
      {error ? <p role="alert" className="form-error">{error}</p> : null}
      {notice ? <p className="form-message" role="status">{notice}</p> : null}
      <div className="form-actions">
        <button type="button" className="btn btn--primary" onClick={() => { void handleSave(); }} disabled={saving}>
          {saving ? "Guardando…" : "Guardar grupos"}
        </button>
      </div>
    </section>
  );
}
